import { useState, useEffect } from 'react';
import { ConfigurationService } from '../../services/configuration.service';
import { SanitizerService } from '../../services/sanitizer.service';
import { LanguageService } from '../../services/language.service';
import ShowMoreButton from '../Shared/ShowMoreButton';
import Spinner from '../Shared/Spinner';
import BlogListApiModel from './models/blog-list.api-model';
import Alert from '../Shared/Alert';
import { useTexts } from '../../hooks/texts.hook';
import { Status } from '../../hooks/fetch.hook';
import type { IBlogListItem } from '../../models/blog-list-item.model';

// === CONSTANTS ===
const guid = ConfigurationService.get('bloglist.guid');
// Get the category and tag url parameters
const urlParams = new URLSearchParams(window.location.search);
const category = urlParams.get('category');

function BlogListComponent() {
  const { t } = useTexts();

  // === STATE ===
  const [getBlogListItemsStatus, setGetBlogListItemsStatus] = useState(Status.notperformed);
  const [blogListItems, setBlogListItems] = useState<IBlogListItem[]>([]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(0);

  useEffect(() => {
    async function fetchData() {
      try {
        setGetBlogListItemsStatus(Status.loading);
        const categoryQueryString = category ? `&category=${category}` : '';
        const response = await fetch(`/api/blog/list/${guid}?language=${LanguageService.getCurrentLanguage()}&page=${currentPageNumber}${categoryQueryString}`);
        if (!response.ok) {
          setGetBlogListItemsStatus(Status.failed);
          return;
        }
        const data = BlogListApiModel.mapToBusinessModel(await response.json());
        setNumberOfPages(data.numberOfPages);
        setBlogListItems((ps) => ps.concat(data.items));
        setGetBlogListItemsStatus(Status.success);
      } catch {
        setGetBlogListItemsStatus(Status.failed);
      }
    }
    fetchData();
  }, [currentPageNumber]);

  function handleMoreClick() {
    setCurrentPageNumber(currentPageNumber + 1);
  }

  return (
    <div>
      <ol id="blog-list-items" aria-live="polite" className="list-unstyled">
        {blogListItems.map((item) => (
          <li key={item.url} className="mb-4 mb-md-5">
            <a className="d-block text-decoration-none" href={item.url}>
              {item.pictureHtml && (
                <div
                  className="mb-2"
                  dangerouslySetInnerHTML={{
                    __html: SanitizerService.sanitizeHtml(item.pictureHtml),
                  }}
                ></div>
              )}
              <h2 className="text-decoration-underline">{item.heading}</h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: SanitizerService.sanitizeHtml(item.text),
                }}
              ></div>
            </a>
          </li>
        ))}
      </ol>
      <div aria-live="polite">{getBlogListItemsStatus === Status.loading && <Spinner />}</div>
      <Alert show={getBlogListItemsStatus === Status.failed} heading={t.generalError} closable={false} className="mb-3" />
      {getBlogListItemsStatus !== Status.loading && numberOfPages > currentPageNumber + 1 && <ShowMoreButton ariaControls="blog-list-items" onClick={handleMoreClick}></ShowMoreButton>}
    </div>
  );
}

export default BlogListComponent;
